import { useApolloClient } from "@apollo/client";
import { Card, NonIdealState, Spinner } from "@blueprintjs/core";
import { useGetProfileQuery } from "@package/ui-gql/index-data-access";
import { AutoZoomFrame, Wall, WallContextProvider } from "@package/ui-wall";
import { WALL_HEIGHT_PREVIEW_AVAILABLE } from "../../constants/contants";
import { WallPreviewContainer } from "../WallPreviewContainer/WallPreviewContainer";
import { useAppStore } from "./../../store/store";

type WallPreviewProfileContentsProps = {
  profile?: string;
};

function WallPreviewProfileContents({
  profile,
}: WallPreviewProfileContentsProps) {
  const client = useApolloClient();
  const store = useAppStore();

  const { data, loading, error } = useGetProfileQuery({
    variables: {
      id: profile ?? "",
    },
    skip: !profile,
  });

  if (!profile) {
    return (
      <WallPreviewContainer>
        <NonIdealState
          className="min-h-36"
          icon="info-sign"
          title="Select a Canvas to Preview"
        />
      </WallPreviewContainer>
    );
  }

  if (loading) {
    return (
      <WallPreviewContainer>
        <Spinner className="min-h-36" />
      </WallPreviewContainer>
    );
  }

  if (error || !data?.item?.type || !data?.item?.screen) {
    return (
      <WallPreviewContainer>
        <NonIdealState
          className="min-h-36"
          icon="error"
          title="Canvas Screens Misconfigured"
          description="Please check the canvas screen configuration."
        />
      </WallPreviewContainer>
    );
  }

  return (
    <Card compact elevation={1}>
      <WallPreviewContainer>
        <AutoZoomFrame
          type={data.item.type}
          profileScreen={data.item.screen}
          height={WALL_HEIGHT_PREVIEW_AVAILABLE}
        >
          <WallContextProvider
            clientDataAccess={client}
            profile={profile}
            store={store}
            type="local"
            caching={false}
          >
            <Wall />
          </WallContextProvider>
        </AutoZoomFrame>
      </WallPreviewContainer>
    </Card>
  );
}

type WallPreviewProfileProps = {
  profile?: string;
  version?: number | null;
};

export function WallPreviewProfile({
  profile,
  version,
}: WallPreviewProfileProps) {
  return <WallPreviewProfileContents key={version} profile={profile} />;
}
